// orphaned-ticket-reconciler.ts
//
// Bug C (dogfood P10, ME-2026-0097d9): a supervisor crash/kill between
// `attempt_started` and the ticket's terminal event leaves that ticket
// permanently "in_progress" — nothing ever resets it. Spec F01 ("Supervisor
// chết, child sống"): "Restart vào RECOVERING ... chỉ cleanup sau khi xác
// nhận hết writers." — runs at `run` STARTUP, reclaims only once it can
// show no live writer is left for the ticket's most recent attempt.
//
// Liveness signal: the binding file (`autopilot-binding-file-writer.ts`)
// carries a config-derived `expires_at` (deadline + shutdown_grace +
// lease_stale_sec, D2); a clean attempt end always deletes it. Still on
// disk past `expires_at` => safe to reclaim (F01's "no live writer, not
// merely slow"). Unknown liveness before that instant is never "dead".
import { promises as fs } from "node:fs";
import { join } from "node:path";
// D12: evidence string-building split out — see that module's header.
import {
  bindingCorruptEvidence,
  bindingExpiresAtUnparseableEvidence,
  noBindingSupervisorCrashEvidence,
  workerOrphanedEvidence,
} from "./orphaned-ticket-recovery-evidence.js";
import { unlockMarkerAppliesToTicket } from "./orphaned-ticket-unlock-marker-match.js";
import type { Ticket } from "./schemas/ticket-schema.js";
// Req 9 (NEW-3, H1 fix): "never spawned" vs "unlock killed it" — see headers.
import { readTicketLatestAttemptStartedAtIso } from "./supervisor-attempt-directory-reader.js";
import { readUnlockKilledWorkerMarker } from "./unlock-killed-worker-marker.js";

export interface OrphanedTicketBindingLease {
  expires_at: string;
}

/**
 * Review 260909-p6-m2 INFO: `defaultReadBindingLease` used to catch EVERY
 * error (missing file AND corrupt/unparsable JSON) and return `undefined`
 * either way — a corrupt binding file was silently treated as "no binding",
 * which meant a `worker_orphaned`/`supervisor_crash` auto-reclaim ran even
 * though the binding file's true state was simply unknown (tampered? disk
 * corruption? partial write?), not provably absent. `readBindingLease` now
 * distinguishes the two: `undefined` stays "no file at all" (unchanged
 * contract — see the four `undefined` call sites in the test file),
 * `{ corrupt: true }` is new and means "file exists but could not be read as
 * the expected shape" — the caller below treats that as UNSAFE to reclaim.
 */
export type OrphanedTicketBindingLeaseRead =
  | OrphanedTicketBindingLease
  | { corrupt: true }
  | undefined;

export interface DetectOrphanedTicketsInput {
  tickets: Ticket[];
  ticketStatus: Record<string, string>;
  missionWorkspace: string;
  now: Date;
  /** Injectable for tests — defaults to reading the real binding file. */
  readBindingLease?: (missionWorkspace: string) => Promise<OrphanedTicketBindingLeaseRead>;
  /** Req 9 (NEW-3, H1 fix): absent => evidence stays neutral. Present => marker vs. per-ticket attempt-started decides naming (see orphaned-ticket-unlock-marker-match.ts). */
  controlRoot?: string;
  /** Injectable for tests — defaults to reading the real marker file. */
  readUnlockMarker?: (
    controlRoot: string,
  ) => Promise<{ killed_worker_pgid: number; released_at: string; ticket?: string } | undefined>;
  /** Injectable for tests — defaults to the real `attempts/<ticketId>-<n>/` mtime reader (H1 fix). */
  readTicketAttemptStartedAt?: (
    controlRoot: string,
    ticketId: string,
  ) => Promise<string | undefined>;
}

export interface OrphanedTicketRecovery {
  ticketId: string;
  reason: "supervisor_crash" | "worker_orphaned" | "binding_corrupt";
  /** Human-readable evidence for the journal/forensics manifest — never a secret. */
  evidence: string;
}

/** Shared with orphaned-ticket-recovery-applier.ts — one source of truth for the binding-file path. */
export function bindingPathFor(missionWorkspace: string): string {
  return join(missionWorkspace, ".claude", "state", "autopilot-binding.json");
}

async function defaultReadBindingLease(
  missionWorkspace: string,
): Promise<OrphanedTicketBindingLeaseRead> {
  let raw: string;
  try {
    raw = await fs.readFile(bindingPathFor(missionWorkspace), "utf8");
  } catch {
    return undefined; // No file at all — unchanged "supervisor_crash" path.
  }
  try {
    const parsed = JSON.parse(raw) as Partial<OrphanedTicketBindingLease>;
    if (typeof parsed.expires_at === "string") return { expires_at: parsed.expires_at };
    // Valid JSON but missing/wrong-typed `expires_at` — same "not provably
    // absent" evidence as an unparsable file; fail closed the same way.
    return { corrupt: true };
  } catch {
    // File EXISTS but is not valid JSON — evidence of tampering/corruption,
    // not evidence of "no binding". Fail closed: never auto-reclaim.
    return { corrupt: true };
  }
}

/**
 * Finds `in_progress` tickets whose most recent attempt has no live writer
 * left. Returns one recovery plan entry per orphaned ticket — callers
 * decide how to journal/apply it. NEVER mutates anything (pure read + pure
 * decision) — safe on every `run` startup unconditionally.
 *
 * Three outcomes: no binding file at all => `supervisor_crash` (NEW-3: does
 * NOT prove "never spawned" — `autopilot unlock` also removes the binding
 * after killing a live worker, see orphaned-ticket-recovery-evidence.ts);
 * binding present but unreadable/wrong-shaped or `expires_at` unparseable
 * => `binding_corrupt` (UNKNOWN, never auto-reclaim); binding present and
 * `now` at/after `expires_at` => `worker_orphaned`. Not yet expired => NOT
 * orphaned, caller leaves the ticket alone.
 */
export async function detectOrphanedInProgressTickets(
  input: DetectOrphanedTicketsInput,
): Promise<OrphanedTicketRecovery[]> {
  const inProgress = input.tickets.filter((t) => input.ticketStatus[t.id] === "in_progress");
  if (inProgress.length === 0) return [];

  const readLease = input.readBindingLease ?? defaultReadBindingLease;
  const lease = await readLease(input.missionWorkspace);

  const unlockMarker =
    lease === undefined && input.controlRoot !== undefined
      ? await (input.readUnlockMarker ?? readUnlockKilledWorkerMarker)(input.controlRoot)
      : undefined;
  const readAttemptStartedAt =
    input.readTicketAttemptStartedAt ?? readTicketLatestAttemptStartedAtIso;

  const recoveries: OrphanedTicketRecovery[] = [];
  for (const ticket of inProgress) {
    if (lease === undefined) {
      // H1 fix: never attribute the mission-wide marker to the wrong ticket.
      const markerIsNewerThanAttempt = await unlockMarkerAppliesToTicket(
        unlockMarker,
        ticket.id,
        input.controlRoot,
        readAttemptStartedAt,
      );
      recoveries.push({
        ticketId: ticket.id,
        reason: "supervisor_crash",
        evidence: noBindingSupervisorCrashEvidence(
          ticket.id,
          unlockMarker,
          markerIsNewerThanAttempt,
        ),
      });
      continue;
    }
    if ("corrupt" in lease) {
      recoveries.push({
        ticketId: ticket.id,
        reason: "binding_corrupt",
        evidence: bindingCorruptEvidence(ticket.id),
      });
      continue;
    }
    const expiresAtMs = Date.parse(lease.expires_at);
    // Audit 2026-09-14 (M4): a meaningless `expires_at` (e.g. "not-a-date")
    // parses to NaN — treat as UNKNOWN (binding_corrupt), never as "lease
    // lapsed" (that would auto-reclaim on liveness this module cannot know).
    if (!Number.isFinite(expiresAtMs)) {
      recoveries.push({
        ticketId: ticket.id,
        reason: "binding_corrupt",
        evidence: bindingExpiresAtUnparseableEvidence(ticket.id, lease.expires_at),
      });
      continue;
    }
    const stillWithinLease = input.now.getTime() < expiresAtMs;
    if (stillWithinLease) continue; // Có thể còn sống — KHÔNG đụng vào (unknown != dead).
    recoveries.push({
      ticketId: ticket.id,
      reason: "worker_orphaned",
      evidence: workerOrphanedEvidence(ticket.id, lease.expires_at, input.now.toISOString()),
    });
  }
  return recoveries;
}

// Applying recoveries (binding cleanup, attempt-state bump, journal commit,
// and the binding_corrupt fail-closed forensics path) moved to
// orphaned-ticket-recovery-applier.ts (split, review 260909-p6-m2 INFO — this
// file grew past 200 lines once binding_corrupt landed on top of the
// original Bug C reclaim logic). Re-exported here so existing imports from
// orphaned-ticket-reconciler.js (`cleanupStaleOrphanedBinding`,
// `applyOrphanedTicketRecoveries`) keep working.
export {
  applyOrphanedTicketRecoveries,
  cleanupStaleOrphanedBinding,
} from "./orphaned-ticket-recovery-applier.js";
